import { gatewayApiAgent } from 'common/api/apiAgents';
import { CountryCode } from 'common/api-types/common';
import {
  ListMetaVendorDocuments,
  ListMetaVendors,
  Vendor,
  VendorDocumentCreateRequest,
  VendorDocumentUpdateRequest,
  VendorCreateRequest,
  VendorPOEmailSettingsUpdateRequest,
  VendorSelectedVenuesUpdateRequest,
  VendorUpdateRequest,
  VendorVenueSettingsUpdateRequest,
  VendorExternalSystemsAssignRequest,
  VendorDocument,
  VendorExternalSystemsList,
  ExternalSystemsList,
  VendorErpIdPatchRequest,
  VendorsRequestParams,
  BaseTermsList,
  CampaignsRequestParams,
  ListMetaCampaigns,
  SingleCampaignRequestParams,
  CampaignItems,
  VendorsSimplified,
  VendorSimplifiedFilter,
  POSendingSystem,
  POSendingSystemsList,
  POSendingSystemUpdateRequest,
  DistributorsResponse,
  DistributorsUpdateRequest,
  DownloadCampaignsByNamesParams,
  BaseTermUpdateRequest,
  BaseTermCreateRequest,
  BaseTerm,
  FundingBudget,
  FundingBudgetsList,
  FundingBudgetCreateRequest,
  FundingBudgetUpdateRequest,
  CampaignsDraft,
  CampaignsDraftInformation,
  VendorProductGroupings,
  FundingBudgetDeleteRequest,
  ClawbackEmailTemplate,
  ClawbackEmailTemplateCreateRequest,
  ClawbackEmailTemplateListResponse,
  ClawbackEmailTemplateUpdateRequest,
  ClawbackEmailTemplatesRequestParams,
} from 'common/api-types/vendor';

export async function fetchVendors(params: VendorsRequestParams) {
  const response = await gatewayApiAgent.get<ListMetaVendors>('/vendor/public/v1/vendors', {
    params,
  });
  return response.data;
}

export async function fetchVendorDetails(vendorId: Vendor['id']) {
  const response = await gatewayApiAgent.get<Vendor>(`/vendor/public/v1/vendors/${vendorId}`);
  return response.data;
}

export async function createVendor(request: VendorCreateRequest) {
  await gatewayApiAgent.post<void>('/vendor/public/v1/vendors', request);
}

export async function updateVendor(request: VendorUpdateRequest) {
  await gatewayApiAgent.put<void>(`/vendor/public/v1/vendors/${request.id}`, request);
}

export async function patchVendorErpId(id: string, request: VendorErpIdPatchRequest) {
  await gatewayApiAgent.patch<void>(`/vendor/public/v1/vendors/${id}`, request);
}

export async function deleteVendor(id: Vendor['id']) {
  return await gatewayApiAgent.delete<void, void, string>(`/vendor/public/v1/vendors/${id}`);
}

export async function updateVendorPurchaseOrderSettings(
  id: Vendor['id'],
  request: VendorPOEmailSettingsUpdateRequest,
) {
  await gatewayApiAgent.put<void>(`/vendor/public/v1/vendors/${id}/po-email-settings`, request);
}

export async function updateVendorVenueSettings(
  id: Vendor['id'],
  request: VendorVenueSettingsUpdateRequest,
) {
  return await gatewayApiAgent.put<void, void, VendorVenueSettingsUpdateRequest>(
    `/vendor/public/v1/vendors/${id}/venue-settings`,
    request,
  );
}

export async function updateVendorExternalSystems(
  id: Vendor['id'],
  request: VendorExternalSystemsAssignRequest,
) {
  return await gatewayApiAgent.post<void, void, VendorExternalSystemsAssignRequest>(
    `/vendor-integrations/public/v1/vendors/${id}/external-systems`,
    request,
  );
}

export async function updateVendorSelectedVenues(
  id: Vendor['id'],
  request: VendorSelectedVenuesUpdateRequest,
) {
  const response = await gatewayApiAgent.put(
    `/vendor/public/v1/vendors/${id}/selected-venues`,
    request,
  );
  return response.data;
}

export async function fetchVendorDocuments(id: Vendor['id']) {
  const response = await gatewayApiAgent.get<ListMetaVendorDocuments>(
    `/vendor/public/v1/vendors/${id}/documents`,
  );
  return response.data;
}

export async function fetchExternalSystems() {
  const response = await gatewayApiAgent.get<ExternalSystemsList>(
    `/vendor-integrations/public/v1/external-systems`,
  );
  return response.data;
}

export async function fetchVendorExternalSystems(id: Vendor['id']) {
  const response = await gatewayApiAgent.get<VendorExternalSystemsList>(
    `/vendor-integrations/public/v1/vendors/${id}/external-systems`,
  );
  return response.data;
}

export async function createVendorDocument(id: Vendor['id'], request: VendorDocumentCreateRequest) {
  await gatewayApiAgent.post<void>(`/vendor/public/v1/vendors/${id}/documents`, request);
}

export async function updateVendorDocument(
  vendorId: Vendor['id'],
  documentId: VendorDocument['id'],
  request: VendorDocumentUpdateRequest,
) {
  await gatewayApiAgent.put<void>(
    `/vendor/public/v1/vendors/${vendorId}/documents/${documentId}`,
    request,
  );
}

export async function removeVendorDocument(
  vendorId: Vendor['id'],
  documentId: VendorDocument['id'],
) {
  await gatewayApiAgent.delete<void>(
    `/vendor/public/v1/vendors/${vendorId}/documents/${documentId}`,
  );
}

export async function downloadDeliverySchedules(countryCodes: CountryCode[]) {
  const response = await gatewayApiAgent.get<Blob>(
    `/vendor/public/v1/vendor-logistics/delivery-schedules/csv`,
    { responseType: 'blob', params: { countryCodes } },
  );
  return response.data;
}

export const uploadDeliverySchedules = async (file: File, countryCodes: CountryCode[]) => {
  const response = await gatewayApiAgent.post(
    `/vendor/public/v1/vendor-logistics/delivery-schedules/csv`,
    file,
    { headers: { 'Content-Type': 'text/csv' }, params: { countryCodes } },
  );
  return response.data;
};

export async function downloadDeliveryCalendar(countryCodes: CountryCode[]) {
  const response = await gatewayApiAgent.get<Blob>(`/ordering-automation/public/v1/calendars/csv`, {
    responseType: 'blob',
    params: { countryCodes },
  });
  return response.data;
}

export async function downloadOrderingConditions(countryCodes: CountryCode[]) {
  const response = await gatewayApiAgent.get<Blob>(
    `/vendor/public/v1/vendor-logistics/ordering-conditions/csv`,
    { responseType: 'blob', params: { countryCodes } },
  );
  return response.data;
}

export const uploadOrderingConditions = async (file: File, countryCodes: CountryCode[]) => {
  const response = await gatewayApiAgent.post(
    `/vendor/public/v1/vendor-logistics/ordering-conditions/csv`,
    file,
    { headers: { 'Content-Type': 'text/csv' }, params: { countryCodes } },
  );
  return response.data;
};

export async function downloadOrderingConditionsExample() {
  const response = await gatewayApiAgent.get<Blob>(
    `/vendor/public/v1/vendor-logistics/ordering-conditions/example-csv`,
    { responseType: 'blob' },
  );
  return response.data;
}

export async function downloadDeliverySchedulesExample() {
  const response = await gatewayApiAgent.get<Blob>(
    `/vendor/public/v1/vendor-logistics/delivery-schedules/example-csv`,
    { responseType: 'blob' },
  );
  return response.data;
}

export async function getCampaigns(params: CampaignsRequestParams, signal?: AbortSignal) {
  const response = await gatewayApiAgent.get<ListMetaCampaigns>(
    `vendor-management/public/v1/fundings/campaigns`,
    { params, signal },
  );
  return response.data;
}

export async function getSingleCampaign(params: SingleCampaignRequestParams) {
  const response = await gatewayApiAgent.get<CampaignItems>(
    `vendor-management/public/v1/fundings/campaigns/campaign`,
    { params },
  );
  return response.data;
}

export async function downloadCampaignsExample() {
  const response = await gatewayApiAgent.get<Blob>(
    `/vendor-management/public/v1/fundings/campaigns/example-csv`,
    { responseType: 'blob' },
  );
  return response.data;
}

export const getVendorBaseTerms = async (vendorId: string) => {
  const response = await gatewayApiAgent.get<{ items: BaseTermsList }>(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/base-terms`,
  );
  return response.data;
};

export const getBaseTerm = async (vendorId: string, termId: string) => {
  const response = await gatewayApiAgent.get<BaseTerm>(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/base-terms/${termId}`,
  );
  return response.data;
};

export const createBaseTerm = async (vendorId: string, request: BaseTermCreateRequest) => {
  const response = await gatewayApiAgent.post(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/base-terms`,
    request,
  );
  return response.data;
};

export const updateBaseTerm = async (
  vendorId: string,
  termId: string,
  request: BaseTermUpdateRequest,
) => {
  const response = await gatewayApiAgent.put(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/base-terms/${termId}`,
    request,
  );
  return response.data;
};

export const deleteBaseTerm = async (vendorId: string, termId: string) => {
  const response = await gatewayApiAgent.delete(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/base-terms/${termId}`,
  );
  return response.data;
};

export const getVendorFundingBudgets = async (vendorId: string) => {
  const response = await gatewayApiAgent.get<FundingBudgetsList>(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/budgets`,
  );
  return response.data;
};

export const getVendorFundingBudget = async (vendorId: string, budgetId: string) => {
  const response = await gatewayApiAgent.get<FundingBudget>(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/budgets/${budgetId}`,
  );
  return response.data;
};

export const createVendorFundingBudget = async (
  vendorId: string,
  request: FundingBudgetCreateRequest,
) => {
  const response = await gatewayApiAgent.post<FundingBudget>(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/budgets`,
    request,
  );
  return response.data;
};

export const updateVendorFundingBudget = async (
  vendorId: string,
  budgetId: string,
  request: FundingBudgetUpdateRequest,
) => {
  const response = await gatewayApiAgent.put<FundingBudget>(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/budgets/${budgetId}`,
    request,
  );
  return response.data;
};

export const deleteVendorFundingBudget = async (
  vendorId: string,
  budgetId: string,
  request: FundingBudgetDeleteRequest,
) => {
  await gatewayApiAgent.delete(
    `vendor-management/public/v1/fundings/vendors/${vendorId}/budgets/${budgetId}`,
    { data: request },
  );
};

export const getVendorProductGroupings = async (vendorId: string) => {
  const response = await gatewayApiAgent.get<VendorProductGroupings>(
    `/vendor-management/public/v1/fundings/vendors/${vendorId}/product-groupings`,
  );
  return response.data;
};

export async function fetchSimplifiedVendors(params: VendorSimplifiedFilter) {
  const response = await gatewayApiAgent.post<VendorsSimplified>(
    '/vendor/public/v1/vendors/simplified',
    params,
  );
  return response.data;
}

export async function fetchVendorPOSendingSystem(id: Vendor['id']) {
  const response = await gatewayApiAgent.get<POSendingSystem>(
    `/vendor/public/v1/vendor-integrations/${id}/external-system-info`,
  );
  return response.data;
}

export async function updateVendorPOSendingSystem(
  id: Vendor['id'],
  request: POSendingSystemUpdateRequest,
) {
  const response = await gatewayApiAgent.put<POSendingSystem>(
    `/vendor/public/v1/vendor-integrations/${id}/external-system-info`,
    request,
  );
  return response.data;
}

export async function fetchPOSendingSystems() {
  const response = await gatewayApiAgent.get<{ systemList: POSendingSystemsList }>(
    `/vendor/public/v1/vendor-integrations/external-systems`,
  );
  return response.data;
}

export async function fetchVendorDistributors(id: string) {
  const response = await gatewayApiAgent.get<DistributorsResponse>(
    `/vendor/public/v1/vendors/${id}/distributors`,
  );
  return response.data;
}

export async function updateVendorDistributors(id: string, request: DistributorsUpdateRequest) {
  const response = await gatewayApiAgent.put<DistributorsResponse>(
    `/vendor/public/v1/vendors/${id}/distributors`,
    request,
  );
  return response.data;
}

export async function downloadCampaignsByNames(params: DownloadCampaignsByNamesParams) {
  const response = await gatewayApiAgent.get<Blob>(
    `/vendor-management/public/v1/fundings/campaigns/csv/by-names`,
    { responseType: 'blob', params },
  );
  return response.data;
}

export async function uploadCampaignsDraft(file: File, countryCodes: CountryCode[]) {
  const response = await gatewayApiAgent.post<CampaignsDraft>(
    `/vendor-management/public/v1/fundings/campaigns/drafts`,
    file,
    { headers: { 'Content-Type': 'text/csv' }, params: { countryCodes } },
  );
  return response.data;
}

export async function confirmCampaignsDraft(draftId: string) {
  const response = await gatewayApiAgent.post<void>(
    `/vendor-management/public/v1/fundings/campaigns/drafts/${draftId}/confirm`,
  );
  return response.data;
}

export async function discardCampaignsDraft(draftId: string) {
  const response = await gatewayApiAgent.post<void>(
    `/vendor-management/public/v1/fundings/campaigns/drafts/${draftId}/discard`,
  );
  return response.data;
}

export async function getCampaignsDraftInformation(draftId: string) {
  const response = await gatewayApiAgent.get<CampaignsDraftInformation>(
    `/vendor-management/public/v1/fundings/campaigns/drafts/${draftId}/information`,
  );
  return response.data;
}

export async function fetchClawbackEmailTemplates(params?: ClawbackEmailTemplatesRequestParams) {
  const response = await gatewayApiAgent.get<ClawbackEmailTemplateListResponse>(
    '/vendor/public/v1/clawbacks/email-templates',
    { params },
  );
  return response.data;
}

export async function fetchClawbackEmailTemplate(templateId: string) {
  const response = await gatewayApiAgent.get<ClawbackEmailTemplate>(
    `/vendor/public/v1/clawbacks/email-templates/${templateId}`,
  );
  return response.data;
}

export async function createClawbackEmailTemplate(request: ClawbackEmailTemplateCreateRequest) {
  const response = await gatewayApiAgent.post<ClawbackEmailTemplate>(
    '/vendor/public/v1/clawbacks/email-templates',
    request,
  );
  return response.data;
}

export async function updateClawbackEmailTemplate(
  templateId: string,
  request: ClawbackEmailTemplateUpdateRequest,
) {
  const response = await gatewayApiAgent.put<ClawbackEmailTemplate>(
    `/vendor/public/v1/clawbacks/email-templates/${templateId}`,
    request,
  );
  return response.data;
}

export async function deleteClawbackEmailTemplate(templateId: string) {
  await gatewayApiAgent.delete<void>(`/vendor/public/v1/clawbacks/email-templates/${templateId}`);
}
